import {
  and,
  eq,
  post,
  post_comment,
  post_like,
  post_report,
  type PowerSyncDrizzleDB,
} from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { serializeMedia } from "./feed";
import { getUserLikeForPostQuery, getUserReportForPostQuery } from "./queries";

/** Identidad desnormalizada del autor (snapshot para el feed público). */
export interface PostAuthor {
  name: string | null;
  image: string | null;
}

export interface CreatePostInput {
  caption?: string | null;
  media?: readonly string[];
  productId?: string | null;
  rating?: number | null;
}

/**
 * 📣 CREAR PUBLICACIÓN — INSERT sobre la vista local-first. El servidor re-estampa
 * el `user_id` autenticado y el snapshot autoritativo de autor (write-path). Las
 * imágenes se serializan al array JSON del campo `media`. Devuelve el id nuevo.
 */
export async function createPostMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  author: PostAuthor,
  input: CreatePostInput,
): Promise<{ id: string }> {
  const now = nowISO();
  const id = generateUUID();
  await db.insert(post).values({
    id,
    user_id: userId,
    author_name: author.name,
    author_image: author.image,
    caption: input.caption?.trim() ?? null,
    product_id: input.productId ?? null,
    media: serializeMedia(input.media ?? []),
    rating: input.rating ?? null,
    visibility: "public",
    deleted_at: null,
    created_at: now,
    updated_at: now,
  });
  return { id };
}

/** Envía una publicación a la papelera (soft-delete). Sólo el autor (aislamiento). */
export async function softDeletePostMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(post)
    .set({ deleted_at: now, updated_at: now })
    .where(and(eq(post.user_id, userId), eq(post.id, id)));
}

/**
 * ❤️ TOGGLE LIKE — patrón SELECT→DELETE|INSERT (las vistas PowerSync no admiten
 * UPSERT, gotcha #15). Quitar el like es hard-delete; darlo es un INSERT con
 * unicidad (post_id, user_id). Devuelve el estado final `liked`.
 */
export async function toggleLikeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  postId: string,
): Promise<{ liked: boolean }> {
  const existing = await getUserLikeForPostQuery(db, postId, userId);
  const current = existing[0];
  if (current) {
    await db
      .delete(post_like)
      .where(and(eq(post_like.user_id, userId), eq(post_like.id, current.id)));
    return { liked: false };
  }
  await db.insert(post_like).values({
    id: generateUUID(),
    post_id: postId,
    user_id: userId,
    created_at: nowISO(),
  });
  return { liked: true };
}

/** 💬 Añade un comentario a una publicación. Devuelve el id nuevo. */
export async function addCommentMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  author: PostAuthor,
  postId: string,
  body: string,
): Promise<{ id: string }> {
  const now = nowISO();
  const id = generateUUID();
  await db.insert(post_comment).values({
    id,
    post_id: postId,
    user_id: userId,
    author_name: author.name,
    author_image: author.image,
    body: body.trim(),
    deleted_at: null,
    created_at: now,
    updated_at: now,
  });
  return { id };
}

/** Envía un comentario a la papelera (soft-delete). Sólo el autor (aislamiento). */
export async function softDeleteCommentMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(post_comment)
    .set({ deleted_at: now, updated_at: now })
    .where(and(eq(post_comment.user_id, userId), eq(post_comment.id, id)));
}

export interface ReportPostResult {
  id: string;
  /** true si ya existía un reporte del usuario (idempotente, se actualizó). */
  alreadyReported: boolean;
}

/**
 * 🚩 REPORTAR PUBLICACIÓN — UPDATE-or-INSERT por (post_id, user_id): re-reportar
 * es idempotente (actualiza motivo/detalles). El estado de moderación lo gobierna
 * el servidor (Wave 1-G); el cliente sólo crea el reporte en `pending`.
 */
export async function reportPostMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  postId: string,
  reason: string,
  details?: string | null,
): Promise<ReportPostResult> {
  const now = nowISO();
  const existing = await getUserReportForPostQuery(db, postId, userId);
  const current = existing[0];
  if (current) {
    await db
      .update(post_report)
      .set({ reason, details: details ?? null })
      .where(and(eq(post_report.user_id, userId), eq(post_report.id, current.id)));
    return { id: current.id, alreadyReported: true };
  }
  const id = generateUUID();
  await db.insert(post_report).values({
    id,
    post_id: postId,
    user_id: userId,
    reason,
    details: details ?? null,
    status: "pending",
    created_at: now,
  });
  return { id, alreadyReported: false };
}
